import { createOrganizerAccountMenu } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createCard } from "../../components/ui/card";
import { createDisclosure } from "../../components/ui/disclosure";
import { createHeading } from "../../components/ui/heading";
import { createPageContainer } from "../../components/ui/page-container";
import { createSkipLink } from "../../components/ui/skip-link";
import { createText } from "../../components/ui/text";
import { fetchSessionMe } from "../../lib/api-auth";
import type { Translate } from "../../lib/i18n";
import {
  clearOrganizerSessionClientHint,
  readOrganizerSessionClientHint,
  writeOrganizerSessionSnapshotHint,
} from "../../lib/organizer-session-client-hint";
import type { SupportedLocale } from "../../locales/catalog-registry";

export interface RenderHomeOptions {
  /** Active UI locale (**`SupportedLocale`**), used by the header locale switcher. */
  locale: SupportedLocale;
  /** Cancels in-flight session probe when navigation tears the view down. */
  signal: AbortSignal;
}

function isMePayload(
  data: unknown,
): data is { user_id: string; display_name?: string | null } {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const rec = data as Record<string, unknown>;
  return typeof rec.user_id === "string" && rec.user_id.length > 0;
}

/**
 * Styled in-app anchor with **`qz-btn`** classes for SPA navigation (**`quiz-frontend`** shell).
 *
 * @param href - Relative path for client routing.
 * @param label - Catalog-backed caption.
 * @param variant - Primary or outlined secondary (**`qz-btn`**).
 */
function createNavButtonLink(
  href: string,
  label: string,
  variant: "primary" | "secondary",
): HTMLAnchorElement {
  const a = document.createElement("a");
  a.href = href;
  a.textContent = label;
  a.className =
    variant === "primary"
      ? "qz-btn qz-btn--primary"
      : "qz-btn qz-btn--secondary";
  return a;
}

/** Compact person glyph for signed-in participant guidance row (**`ux/home-page`** impression). */
function createSignedInGuidancePersonGlyph(): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg") as SVGSVGElement;
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.classList.add("home-signed-in-guidance__glyph");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke-width", "1.65");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");

  const c = document.createElementNS(ns, "circle");
  c.setAttribute("cx", "12");
  c.setAttribute("cy", "8");
  c.setAttribute("r", "3.35");
  const p = document.createElementNS(ns, "path");
  p.setAttribute("d", "M5 20v-1.2c0-2 3.55-4.3 7-4.3s7 2.28 7 4.3V20");
  svg.appendChild(c);
  svg.appendChild(p);
  return svg;
}

/** Outline icon pair for organizers / participant cards (accent stroke). */
function createRoleGlyph(kind: "group" | "person"): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg") as SVGSVGElement;
  svg.setAttribute("width", "28");
  svg.setAttribute("height", "28");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.classList.add("home-role-card__glyph");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke-width", "1.6");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");

  if (kind === "person") {
    const c = document.createElementNS(ns, "circle");
    c.setAttribute("cx", "12");
    c.setAttribute("cy", "8");
    c.setAttribute("r", "3.25");
    const p = document.createElementNS(ns, "path");
    p.setAttribute("d", "M5 20v-1.2c0-2 3.55-4.3 7-4.3s7 2.28 7 4.3V20");
    svg.appendChild(c);
    svg.appendChild(p);
    return svg;
  }

  const g1 = document.createElementNS(ns, "circle");
  g1.setAttribute("cx", "8.75");
  g1.setAttribute("cy", "8");
  g1.setAttribute("r", "2.85");
  const b1 = document.createElementNS(ns, "path");
  b1.setAttribute(
    "d",
    "M4 18.75v-.9c0-1.5 2.85-3.05 5.85-3.05s4.9 1.25 5.85 3.05",
  );
  svg.appendChild(g1);
  svg.appendChild(b1);

  const g2 = document.createElementNS(ns, "circle");
  g2.setAttribute("cx", "15");
  g2.setAttribute("cy", "7.65");
  g2.setAttribute("r", "2.5");
  const b2 = document.createElementNS(ns, "path");
  b2.setAttribute("d", "M12 18v-.95c0-1.85 3.5-3.95 8-4.95");
  svg.appendChild(g2);
  svg.appendChild(b2);

  return svg;
}

/** Single accent bullet row matching the **`ux/home-page`** card mockups. */
function createRoleBullet(label: string): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "home-role-card__item";
  const mark = document.createElement("span");
  mark.className = "home-role-card__check";
  mark.setAttribute("aria-hidden", "true");
  mark.textContent = "\u2713";
  const txt = document.createElement("span");
  txt.textContent = label;
  li.append(mark, txt);
  return li;
}

/**
 * Wraps organizers / participant explainer surfaces that mirror **`ux/home-page`** compositions.
 *
 * @param translate - Translator.
 */
function buildAnonymousPanels(translate: Translate): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "home-cards";

  const orgCard = createCard();
  orgCard.classList.add("home-role-card");

  const orgHead = document.createElement("div");
  orgHead.className = "home-role-card__head";
  orgHead.appendChild(createRoleGlyph("group"));
  orgHead.appendChild(
    createHeading(3, translate("quiz.home.role_organizers_title")),
  );
  orgCard.appendChild(orgHead);

  const orgList = document.createElement("ul");
  orgList.className = "home-role-card__list";
  orgList.append(
    createRoleBullet(translate("quiz.home.role_organizers_bullet_1")),
    createRoleBullet(translate("quiz.home.role_organizers_bullet_2")),
  );
  orgCard.appendChild(orgList);

  orgCard.appendChild(
    createNavButtonLink(
      "/organizer/sign-in",
      translate("quiz.home.cta_sign_in_organizer"),
      "primary",
    ),
  );

  const partCard = createCard();
  partCard.classList.add("home-role-card");

  const partHead = document.createElement("div");
  partHead.className = "home-role-card__head";
  partHead.appendChild(createRoleGlyph("person"));
  partHead.appendChild(
    createHeading(3, translate("quiz.home.role_participants_title")),
  );
  partCard.appendChild(partHead);

  const partList = document.createElement("ul");
  partList.className = "home-role-card__list";
  partList.append(
    createRoleBullet(translate("quiz.home.role_participants_bullet_1")),
    createRoleBullet(translate("quiz.home.role_participants_bullet_2")),
  );
  partCard.appendChild(partList);

  wrap.append(orgCard, partCard);
  return wrap;
}

/**
 * Fills **`region`** when an organizer cookie session is recognised (**`DD-SRS-F-HOME-PAGE.5`**).
 *
 * @param translate - Translator.
 * @param region - Live **`section`** swapped for signed-in content (**`DD-SRS-F-HOME-PAGE.5`**).
 */
function fillSignedInRegion(translate: Translate, region: HTMLElement): void {
  region.replaceChildren();
  region.classList.remove("home-session--minimal");
  region.classList.add("home-session--landing");

  const focus = document.createElement("div");
  focus.className = "home-session__focus";

  const primary = createNavButtonLink(
    "/quizzes/wizard",
    translate("quiz.home.cta_create_quiz_primary"),
    "primary",
  );
  primary.classList.add("home-session__primary-cta");
  focus.appendChild(primary);

  const browse = document.createElement("p");
  browse.className = "home-session__browse";
  const browseLink = document.createElement("a");
  browseLink.href = "/quizzes";
  browseLink.className = "home-session__secondary-link";
  browseLink.textContent = translate("quiz.home.link_view_existing_quizzes");
  browse.appendChild(browseLink);
  focus.appendChild(browse);

  const join = createDisclosure({
    summary: translate("quiz.home.participant_guidance_summary"),
    body: translate("quiz.home.participant_guidance_body"),
  });
  join.classList.add("home-guidance-more");
  const sum = join.querySelector(".qz-disclosure__summary");
  if (sum) {
    const summaryLabel = translate("quiz.home.participant_guidance_summary");
    sum.replaceChildren();
    sum.appendChild(createSignedInGuidancePersonGlyph());
    const lbl = document.createElement("span");
    lbl.className = "home-signed-in-guidance__summary-label";
    lbl.textContent = summaryLabel;
    sum.appendChild(lbl);
  }

  const guidanceShell = document.createElement("div");
  guidanceShell.className = "home-signed-in-guidance";
  guidanceShell.appendChild(join);

  focus.appendChild(guidanceShell);

  region.appendChild(focus);
}

/**
 * Renders the home route using shared **`qz-*`** surfaces and **`ux/home-page`** layout intent.
 *
 * @param mount - Container cleared and filled by this view.
 * @param translate - Localized string resolver.
 * @param options - Locale (**`locale`**) plus lifecycle controls (**`signal`**).
 */
export async function renderHome(
  mount: HTMLElement,
  translate: Translate,
  options: RenderHomeOptions,
): Promise<void> {
  mount.replaceChildren();

  const shell = document.createElement("div");
  shell.className = "home-shell";
  shell.appendChild(
    createSkipLink(translate("quiz.home.skip_to_main"), "home-main"),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  const { header, authSlot } = createStandardSiteAppHeader(
    translate,
    options.locale,
    { localePresentation: "globe" },
  );

  page.appendChild(header);

  const main = document.createElement("main");
  main.id = "home-main";
  main.className = "home-main";

  const heroWrap = document.createElement("header");
  heroWrap.className = "home-hero";

  const heroTitleMount = document.createElement("div");
  const heroSubtitleMount = document.createElement("div");

  heroWrap.appendChild(heroTitleMount);
  heroWrap.appendChild(heroSubtitleMount);
  main.appendChild(heroWrap);

  /** Swaps anonymous hero headings versus signed-in copy. */
  const renderAnonymousHero = (): void => {
    heroTitleMount.replaceChildren();
    heroSubtitleMount.replaceChildren();

    heroTitleMount.appendChild(
      createHeading(1, translate("quiz.home.hero_title")),
    );
    heroSubtitleMount.appendChild(
      createText(translate("quiz.home.hero_subtitle"), {
        muted: true,
      }),
    );
  };

  /**
   * Welcome surface for organizers with an established session (**`DD-SRS-F-HOME-PAGE.5`**).
   *
   * @param payload - **`auth/me`** session; **`display_name`** trims to choose named versus generic headings.
   */
  const renderSignedInHero = (payload: {
    display_name?: string | null;
  }): void => {
    heroTitleMount.replaceChildren();
    heroSubtitleMount.replaceChildren();

    const trimmed =
      typeof payload.display_name === "string"
        ? payload.display_name.trim()
        : "";
    const title =
      trimmed.length > 0
        ? translate("quiz.home.hero_signed_in_named", { name: trimmed })
        : translate("quiz.home.hero_signed_in_generic");

    heroTitleMount.appendChild(createHeading(1, title));
    heroSubtitleMount.appendChild(
      createText(translate("quiz.home.hero_signed_in_subtitle"), {
        muted: true,
      }),
    );
  };

  const sessionRegion = document.createElement("section");
  sessionRegion.className = "home-session";
  sessionRegion.classList.add("home-session--minimal");
  sessionRegion.setAttribute("aria-live", "polite");

  main.appendChild(sessionRegion);

  const anonPanels = buildAnonymousPanels(translate);

  const attachAnonymousPanels = (): void => {
    if (!main.contains(anonPanels)) {
      main.appendChild(anonPanels);
    }
  };

  const detachAnonymousPanels = (): void => {
    anonPanels.remove();
  };

  const footer = document.createElement("footer");
  footer.className = "home-footer";

  const footerInner = document.createElement("div");
  footerInner.className = "home-footer__inner";

  const footerLegalSlot = document.createElement("div");
  footerLegalSlot.className = "home-footer__legal";
  footerLegalSlot.appendChild(
    createText(
      translate("quiz.home.footer_legal", { year: new Date().getFullYear() }),
      { muted: true },
    ),
  );

  const footerAuxNav = document.createElement("nav");
  footerAuxNav.className = "home-footer__aux";
  footerAuxNav.hidden = true;
  footerAuxNav.setAttribute(
    "aria-label",
    translate("quiz.home.footer_aux_aria_label"),
  );

  (
    [
      "quiz.home.footer_link_help",
      "quiz.home.footer_link_privacy",
      "quiz.home.footer_link_terms",
      "quiz.home.footer_link_contact",
    ] as const
  ).forEach((key) => {
    const a = document.createElement("a");
    a.href = "#";
    a.className = "home-footer__aux-link";
    a.textContent = translate(key);
    footerAuxNav.appendChild(a);
  });

  footerAuxNav.addEventListener("click", (evt) => {
    const t = evt.target;
    if (t instanceof HTMLAnchorElement && t.getAttribute("href") === "#") {
      evt.preventDefault();
    }
  });

  footerInner.append(footerLegalSlot, footerAuxNav);
  footer.appendChild(footerInner);

  page.appendChild(main);
  page.appendChild(footer);

  mount.appendChild(shell);

  const setSignedInChrome = (signedIn: boolean): void => {
    if (signedIn) {
      main.classList.add("home-main--signed-in");
      footer.classList.add("home-footer--split");
      footerAuxNav.hidden = false;
      return;
    }
    main.classList.remove("home-main--signed-in");
    footer.classList.remove("home-footer--split");
    footerAuxNav.hidden = true;
  };

  const setAuthHeader = (
    authenticated: boolean,
    session?: Readonly<{ user_id: string; display_name?: string | null }>,
  ): void => {
    authSlot.replaceChildren();
    if (authenticated && session !== undefined) {
      authSlot.appendChild(createOrganizerAccountMenu(translate, session));
    } else {
      authSlot.appendChild(
        createNavButtonLink(
          "/organizer/sign-in",
          translate("quiz.home.header_sign_in"),
          "secondary",
        ),
      );
    }
  };

  const showAnonymousLanding = (): void => {
    setSignedInChrome(false);
    setAuthHeader(false, undefined);
    attachAnonymousPanels();

    renderAnonymousHero();

    sessionRegion.replaceChildren();
    sessionRegion.hidden = true;
  };

  const showAuthenticatedLanding = (payload: {
    user_id: string;
    display_name?: string | null;
  }): void => {
    setSignedInChrome(true);
    setAuthHeader(true, payload);
    detachAnonymousPanels();

    renderSignedInHero(payload);

    sessionRegion.hidden = false;
    sessionRegion.classList.remove("home-session--minimal");
    fillSignedInRegion(translate, sessionRegion);
  };

  const sessionHint = readOrganizerSessionClientHint();

  if (!sessionHint) {
    showAnonymousLanding();
  } else if (sessionHint.kind === "pending") {
    showAuthenticatedLanding({
      user_id: translate("quiz.account.pending_summary"),
      display_name: undefined,
    });
  } else {
    showAuthenticatedLanding({
      user_id: sessionHint.user_id,
      display_name: sessionHint.display_name,
    });
  }

  try {
    const res = await fetchSessionMe({ signal: options.signal });
    if (options.signal.aborted) {
      return;
    }

    if (res.status === 200) {
      const text = await res.text();
      if (options.signal.aborted) {
        return;
      }
      let data: unknown;
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        clearOrganizerSessionClientHint();
        showAnonymousLanding();
        sessionRegion.hidden = false;
        sessionRegion.classList.add("home-session--minimal");
        sessionRegion.replaceChildren();

        const errLine = document.createElement("p");
        errLine.className = "home-session__status";
        errLine.textContent = translate("quiz.home.session_error");
        sessionRegion.appendChild(errLine);
        return;
      }
      if (!isMePayload(data)) {
        clearOrganizerSessionClientHint();
        showAnonymousLanding();
        sessionRegion.hidden = false;
        sessionRegion.classList.add("home-session--minimal");
        sessionRegion.replaceChildren();

        const errLine = document.createElement("p");
        errLine.className = "home-session__status";
        errLine.textContent = translate("quiz.home.session_error");
        sessionRegion.appendChild(errLine);
        return;
      }
      const normalized = {
        user_id: data.user_id,
        display_name:
          typeof data.display_name === "string" ? data.display_name : undefined,
      };
      writeOrganizerSessionSnapshotHint(normalized);
      showAuthenticatedLanding(normalized);
      return;
    }

    if (res.status === 401 || res.status === 403) {
      clearOrganizerSessionClientHint();
      showAnonymousLanding();
      return;
    }

    clearOrganizerSessionClientHint();
    showAnonymousLanding();
    sessionRegion.hidden = false;
    sessionRegion.classList.add("home-session--minimal");
    sessionRegion.replaceChildren();

    const errLine = document.createElement("p");
    errLine.className = "home-session__status";
    errLine.textContent = translate("quiz.home.session_error");
    sessionRegion.appendChild(errLine);
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    clearOrganizerSessionClientHint();
    showAnonymousLanding();
    sessionRegion.hidden = false;
    sessionRegion.classList.add("home-session--minimal");
    sessionRegion.replaceChildren();

    const errLine = document.createElement("p");
    errLine.className = "home-session__status";
    errLine.textContent = translate("quiz.home.session_error");
    sessionRegion.appendChild(errLine);
  }
}
